import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Home, Pin, ChevronsUpDown, Building2, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const GOLD = "#C2A462";
const readPins = () => JSON.parse(localStorage.getItem("bos_pins") || "[]");

function Row({ a, pinned, onEnter, onPin, testPrefix }) {
  return (
    <div className="group flex items-center gap-3 rounded-[3px] border border-[#C2A462]/15 bg-white/[0.02] px-4 py-3 transition-colors hover:border-[#C2A462]/50 hover:bg-[#C2A462]/[0.05]"
      data-testid={`${testPrefix}-${a.id}`}>
      <button onClick={() => onEnter(a.id)} className="flex flex-1 min-w-0 items-center gap-3 text-left" data-testid={`${testPrefix}-enter-${a.id}`}>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#C2A462]/40 text-sm" style={{ color: GOLD, fontFamily: "'Cormorant Garamond', serif" }}>
          {a.name?.[0]?.toUpperCase()}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[14px] text-slate-100">{a.name}</span>
          <span className="block truncate text-[12px] text-slate-500">{a.address || a.business_type || "No address on file"}</span>
        </span>
      </button>
      <button onClick={() => onEnter(a.id)} title="Open entity" className="text-slate-500 hover:text-[#C2A462] transition-colors" data-testid={`${testPrefix}-home-${a.id}`}><Home className="h-4 w-4" /></button>
      {onPin && (
        <button onClick={() => onPin(a.id)} title={pinned ? "Unpin" : "Pin to top"} data-testid={`${testPrefix}-pin-${a.id}`}
          className={`transition-colors ${pinned ? "text-[#C2A462]" : "text-slate-600 hover:text-slate-300"}`}><Pin className={`h-4 w-4 ${pinned ? "fill-current" : ""}`} /></button>
      )}
    </div>
  );
}

function Panel({ onClose }) {
  const { accounts, setAccountId, accountId } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [pins, setPins] = useState(readPins);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const togglePin = (id) => {
    const next = pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id];
    localStorage.setItem("bos_pins", JSON.stringify(next)); setPins(next);
  };
  const enter = (id) => { setAccountId(id); onClose(); nav("/app/dashboard"); };
  const match = (a) => `${a.name} ${a.address || ""}`.toLowerCase().includes(q.toLowerCase());
  const all = useMemo(() => accounts.filter((a) => a.status !== "cancelled" && a.id !== accountId).filter(match).sort((a, b) => a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, accountId, q]);
  const current = accounts.find((a) => a.id === accountId);
  return (
    <div className="fixed inset-0 z-50" onClick={onClose} data-testid="entity-switcher-overlay">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
      <div onClick={(e) => e.stopPropagation()} data-testid="entity-switcher-panel"
        className="absolute left-0 lg:left-[16.5rem] top-0 lg:top-4 h-full lg:h-[calc(100vh-2rem)] w-full sm:w-[480px] flex flex-col rounded-none lg:rounded-r-[4px] border border-[#C2A462]/30 shadow-2xl fade-up"
        style={{ background: "radial-gradient(ellipse at 20% 0%, #13213d 0%, #0b1428 50%, #070d1c 100%)" }}>
        <div className="p-5 pb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for an entity account" data-testid="entity-search-input"
              className="w-full h-10 rounded-[3px] bg-black/30 border border-[#C2A462]/40 pl-10 pr-3 text-[13px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#C2A462]" />
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 p-2" data-testid="entity-switcher-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-6">
          <button onClick={() => { onClose(); nav("/agency"); }} data-testid="switcher-agency-view"
            className="w-full flex items-center gap-3 rounded-[3px] border border-[#C2A462]/40 px-4 py-3 text-left text-[13px] tracking-wide hover:bg-[#C2A462]/10 transition-colors" style={{ color: GOLD }}>
            <Building2 className="h-4 w-4" /> Enterprise View
          </button>
          {current && (
            <div data-testid="current-entity">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3 px-1">Current</div>
              <Row a={current} onEnter={enter} testPrefix="current-entity" />
              <div className="mt-4 h-px bg-[#C2A462]/70" />
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3 px-1">{current ? "Other Entity Accounts (A–Z)" : "All Entity Accounts (A–Z)"}</div>
            <div className="space-y-2">
              {all.map((a) => <Row key={a.id} a={a} pinned={pins.includes(a.id)} onEnter={enter} onPin={togglePin} testPrefix="entity" />)}
              {!all.length && <div className="text-[13px] text-slate-500 px-1">No entity accounts match "{q}".</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EntitySwitcher({ mode }) {
  const { user, account } = useAuth();
  const [open, setOpen] = useState(false);
  const label = mode === "agency" ? "Click here to switch" : account?.name || "Select entity";
  if (user.role !== "agency_owner") {
    return <div className="mx-3 rounded-[3px] border border-[#C2A462]/25 px-4 py-3 text-[13px] text-slate-200 truncate" data-testid="current-account-name">{account?.name}</div>;
  }
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="entity-switcher-btn"
        className="mx-3 flex w-[calc(100%-1.5rem)] min-w-0 items-center justify-between gap-2 rounded-[3px] border border-[#C2A462]/30 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-[#C2A462]/70 hover:bg-[#C2A462]/[0.06]">
        <span className="min-w-0">
          <span className="block text-[11px] tracking-wide text-slate-500">{mode === "agency" ? "Enterprise view" : "Entity account"}</span>
          <span className="block truncate text-[13px]" style={{ color: GOLD }} data-testid="entity-switcher-label">{label}</span>
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {open && <Panel onClose={() => setOpen(false)} />}
    </>
  );
}
